import React from 'react';

import { INSPECTION_PLATE, INSPECTION_X, MIC, SERVER } from './railLayout';
import {
  INK_SOFT,
  INSPECTION_FAIL,
  INSPECTION_PASS,
  PAPER,
} from './railPalette';

/**
 * ### InspectionZone
 *
 * The lift-and-turn station under the microphone. Three parts, back to
 * front: the well in the belt, marked by a dashed rim; the wall of the
 * disc, which stands up out of the well as the disc rises and is scaled
 * flat again as it settles; and the disc itself, carrying a faint green
 * wash to mark it as the place units pass, a red wash that comes up on the
 * server's NG clock, and a ring. At rest the disc sits flush in the well
 * and hides it. Drawn over the belt and under the boxes.
 *
 * @returns The well, the wall and the disc
 */
export function InspectionZone() {
  const { x, y, radiusX, radiusY, innerRadiusX, innerRadiusY, lift } =
    INSPECTION_PLATE;

  return (
    <g>
      <ellipse
        cx={x}
        cy={y}
        rx={radiusX}
        ry={radiusY}
        fill={PAPER}
        stroke={INK_SOFT}
        strokeWidth={1.5}
        strokeDasharray="8 6"
      />
      {/* Sides and the near rim only; the top edge is under the disc */}
      <path
        className="prl-plate-wall"
        d={`M${x - radiusX},${y - lift} V${y} A${radiusX},${radiusY} 0 0 0 ${x + radiusX},${y} V${y - lift}`}
        fill={PAPER}
        stroke={INK_SOFT}
        strokeWidth={1.5}
      />
      <g className="prl-plate-top">
        <ellipse
          cx={x}
          cy={y}
          rx={radiusX}
          ry={radiusY}
          fill={PAPER}
          stroke={INK_SOFT}
          strokeWidth={1.5}
        />
        <ellipse
          cx={x}
          cy={y}
          rx={radiusX}
          ry={radiusY}
          fill={INSPECTION_PASS}
          opacity={0.14}
          stroke="none"
        />
        <ellipse
          className="prl-zone-ng"
          cx={x}
          cy={y}
          rx={radiusX}
          ry={radiusY}
          fill={INSPECTION_FAIL}
          opacity={0}
          stroke="none"
        />
        <ellipse
          cx={x}
          cy={y}
          rx={innerRadiusX}
          ry={innerRadiusY}
          fill="none"
          stroke="#54555b"
          strokeWidth={1}
        />
      </g>
    </g>
  );
}

/** How far above the scene's moving parts the signal is routed. */
const SIGNAL_CLEARANCE_Y = 62;

/**
 * ### SignalPath
 *
 * The dashed run from the microphone to the analysis server: the sound
 * leaves the capsule's swivel, where it is heard, and arrives at the
 * server's face. It is routed up and over the picker, above the reach of
 * the raised arm, so it never appears to come out of the picker instead.
 * Drawn first, so everything else stands in front of it.
 *
 * @returns The dashed signal path
 */
export function SignalPath() {
  const startX = INSPECTION_X + 6;
  const startY = MIC.boomEndY - 6;
  const endX = SERVER.x;
  const endY = SERVER.y + 30;

  return (
    <path
      className="prl-signal-path"
      d={`M${startX},${startY} C${startX + 40},${SIGNAL_CLEARANCE_Y} ${endX - 40},${SIGNAL_CLEARANCE_Y} ${endX},${endY}`}
      fill="none"
      stroke={INK_SOFT}
      strokeWidth={1.5}
    />
  );
}
